import { usePropertyTrackTabs } from "@/modules/property-tracks/hooks/property-tracker-tab";
import { Track } from "@/modules/property-tracks/types";
import { Box, Stack, useTheme } from "@mui/material";
import TabContentMobileWithError from "./tab-content";
import { TrackActivityProvider } from "@/modules/property-tracks/contexts/track-activity.context";
import LastActivityDate from "@/modules/lead-page/new/components/activity/last-activity-date";
import LastActivityCard from "@/modules/lead-page/new/components/activity/last-acticity-card";
import HeaderButton from "@/modules/lead-page/new/components/task-management/header-button";
import { ActionCard } from "@/modules/lead-page/new/components/mobile/mobile-action-cards";
import { Hammer, LayoutList, List, Newspaper, PencilRuler, Route } from "lucide-react";
import AddTrackComment from "@/modules/property-tracks/components/activity-tab/add-comment";

function MobilePropertyTrackTabs({ track }: Readonly<{ track?: Track }>) {
  const theme = useTheme();

  const {
    selectedTab,
    handleTabURLChange,
    subPropertiesTabVisible,
    isRentalListingVisible,
    isResaleListingVisible,
    isHospitalityListingVisible,
    isConstructionTabVisible,
    isInterestsVisible,
    isTenantsVisible,
    isDrawingProposalsVisible,
  } = usePropertyTrackTabs({ track });

  if (!track) {
    return null;
  }

  const actionCards: ActionCard[] = [
    {
      title: "Activity feed",
      caption: (
        <TrackActivityProvider trackUId={track.uid}>
          <LastActivityDate />
        </TrackActivityProvider>
      ),
      body: (
        <TrackActivityProvider trackUId={track.uid}>
          <LastActivityCard
            addComment={
              <AddTrackComment trackUId={track.uid} lastActivityCardStyle />
            }
          />
        </TrackActivityProvider>
      ),
      onClick: () => handleTabURLChange("Activity"),
      icon: Newspaper,
    },
    {
      title: "Overview",
      caption: "Property details & financial information.",
      onClick: () => handleTabURLChange("Overview"),
      icon: List,
    },
    {
      title: "Documents",
      caption: "Media, contracts, proposals, etc.",
      onClick: () => handleTabURLChange("Documents"),
      icon: List,
    },
    {
      title: "Construction",
      caption: "View construction updates.",
      onClick: () => handleTabURLChange("Construction"),
      show: Boolean(isConstructionTabVisible),
      icon: Hammer,
    },
    {
      title: "Customer ID Info",
      caption: "Customer ID document details.",
      onClick: () => handleTabURLChange("CustomerIdInfo"),
      icon: List,
    },
    {
      title: "Linked Tracks",
      caption: "Other tracks linked to this property.",
      onClick: () => handleTabURLChange("LinkedTracks"),
      icon: Route,
    },
    {
      title: "Sub properties",
      caption: "Sub properties linked to this property.",
      onClick: () => handleTabURLChange("SubProperties"),
      show: Boolean(subPropertiesTabVisible),
      icon: List,
    },
    {
      title: "Active Listing",
      caption: "Listing details.",
      onClick: () => handleTabURLChange("Listing"),
      show: Boolean(
        isRentalListingVisible ||
        isResaleListingVisible ||
        isHospitalityListingVisible,
      ),
      icon: LayoutList,
    },
    {
      title: "Interests",
      caption: "Interests details.",
      onClick: () => handleTabURLChange("Interests"),
      show: Boolean(isInterestsVisible),
      icon: List,
    },
    {
      title: "Tenants",
      caption: "Tenant, Contracts & Payment Plans details.",
      onClick: () => handleTabURLChange("Tenants"),
      show: Boolean(isTenantsVisible),
      icon: List,
    },
    {
      title: "Drawing Proposals",
      caption: "Drawing requests & proposals.",
      onClick: () => handleTabURLChange("DrawingProposals"),
      show: Boolean(isDrawingProposalsVisible),
      icon: PencilRuler,
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw",
        backgroundColor: theme.nawy.colors.semantic.layer.secondary,
        overflow: "hidden",
        padding: theme.nawy.spacing.sm,
        gap: theme.nawy.spacing.sm,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="column" gap={theme.nawy.spacing.sm}>
        {actionCards
          .filter((card) => card.show !== false) // Filter out cards where show is explicitly false
          .map((card) => {
            const IconComponent = card.icon;
            return (
              <HeaderButton
                key={card.title}
                title={card.title}
                caption={card.caption}
                onClick={card.onClick}
                body={card.body}
                icon={
                  <IconComponent
                    color={theme.nawy.colors.semantic.text.accent}
                  />
                }
              />
            );
          })}
      </Stack>

      {selectedTab && (
        <TabContentMobileWithError
          selectedTab={selectedTab}
          track={track}
          handleTabURLChange={handleTabURLChange}
        />
      )}
    </Box>
  );
}

export default MobilePropertyTrackTabs;
